import React, { ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import ErrorIcon from './ErrorIcon';
import { StyledAppBoundary } from './index.styled';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryKey: number;
}

class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, retryKey: 0 };
  }

  static getDerivedStateFromError(error: unknown): Partial<AppErrorBoundaryState> {
    return { hasError: true, error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[AppErrorBoundary]', error.message, errorInfo.componentStack);
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });

    // Auto-reload once when a lazy chunk fails to load (stale deployment).
    // After reload, the browser fetches fresh index.html with correct chunk hashes.
    if (this.isChunkLoadError(error)) {
      const reloadKey = 'app_chunk_reload';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();
      // Only auto-reload if we haven't reloaded in the last 10 seconds (prevent loop)
      if (!lastReload || now - Number(lastReload) > 10_000) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
        return;
      }
    }
  }

  private isChunkLoadError(error: Error): boolean {
    const msg = error.message || '';
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk')
    );
  }

  handleRetry = () => {
    // Increment retryKey to force full unmount/remount of children.
    // This triggers useEffect refetches so components get fresh data
    // instead of re-rendering with the same bad Redux state.
    this.setState((prev) => ({ hasError: false, error: null, retryKey: prev.retryKey + 1 }));
  };

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoBack = () => {
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      return (
        <StyledAppBoundary>
          <ErrorIcon />
          <div style={{ fontSize: 30, marginTop: 4 }}>
            Ah! Something went wrong.
          </div>
          <div style={{ fontSize: 18, textAlign: 'center', color: '#666' }}>
            Brace yourself till we get the error fixed.
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
            <button
              onClick={this.handleRetry}
              style={{
                padding: '10px 24px',
                fontSize: 15,
                fontWeight: 500,
                color: '#fff',
                backgroundColor: '#4CAF50',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
            <button
              onClick={this.handleGoBack}
              style={{
                padding: '10px 24px',
                fontSize: 15,
                fontWeight: 500,
                color: '#333',
                backgroundColor: '#f0f0f0',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Go Back
            </button>
            <button
              onClick={this.handleRefresh}
              style={{
                padding: '10px 24px',
                fontSize: 15,
                fontWeight: 500,
                color: '#333',
                backgroundColor: '#f0f0f0',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Refresh Page
            </button>
          </div>
          {import.meta.env.DEV && this.state.error && (
            <div
              style={{
                marginTop: 24,
                padding: '12px 16px',
                backgroundColor: '#fff3f3',
                border: '1px solid #ffccc7',
                borderRadius: 6,
                fontSize: 13,
                color: '#cf1322',
                maxWidth: 600,
                wordBreak: 'break-word',
                fontFamily: 'monospace',
              }}
            >
              {this.state.error.message}
            </div>
          )}
        </StyledAppBoundary>
      );
    } else {
      return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
    }
  }
}

export default AppErrorBoundary;
